import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  RemoteConfigApp,
  RemoteConfigValueType,
  remoteConfigAppTitles,
  remoteConfigValueTypeTitles,
} from 'src/constants/remote-config/remoteConfig'
import type {
  RemoteConfigFormMode,
  RemoteConfigFormStatus,
  RemoteConfigFormValues,
} from 'src/interfaces/remoteConfig'
import {
  formatJsonText,
  validateRemoteConfigValueText,
} from 'src/utils/remoteConfig/remoteConfigValue'
import { RemoteConfigValueEditor } from './RemoteConfigValueEditor'

const KEY_HINT_CREATE =
  "Lowercase letters, digits, dots and underscores. Key, app and type can't be changed later."
const KEY_HINT_EDIT =
  "Key, app and type can't be changed. Delete the key and create a new one instead."
const VALUE_VISIBILITY_HINT =
  "Every signed-in user of the app gets this value. Don't store secrets or personal data."

const APP_OPTIONS = Object.values(RemoteConfigApp).map((app) => {
  return { value: app, label: remoteConfigAppTitles[app] }
})

const TYPE_OPTIONS = Object.values(RemoteConfigValueType).map((type) => {
  return { value: type, label: remoteConfigValueTypeTitles[type] }
})

interface Props {
  mode: RemoteConfigFormMode
}

const RemoteConfigFormContentComponent = ({ mode }: Props) => {
  const { values, errors, touched, status, setFieldValue, setFieldError, setFieldTouched } =
    useFormikContext<RemoteConfigFormValues>()
  const isCreate = mode === 'create'
  const isReadOnly = mode === 'view'
  const hasKeyError = Boolean(touched.key && errors.key)
  const formStatus = status as RemoteConfigFormStatus | undefined

  // A value typed for one type means nothing for another, so the editor starts over.
  const handleTypeChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setFieldValue('type', event.target.value)
      setFieldValue('valueText', '')
      setFieldValue('valueBoolean', true)
    },
    [setFieldValue],
  )

  const handleFormat = useCallback(() => {
    const formatted = formatJsonText(values.valueText)

    if (formatted === null) {
      setFieldTouched('valueText', true, false)
      setFieldError(
        'valueText',
        validateRemoteConfigValueText(RemoteConfigValueType.Json, values.valueText) ?? '',
      )

      return
    }

    setFieldValue('valueText', formatted)
  }, [values.valueText, setFieldValue, setFieldError, setFieldTouched])

  return (
    <div css={tw`flex flex-col gap-4`}>
      <div css={tw`flex flex-col gap-1`}>
        <FormikTextField<RemoteConfigFormValues>
          name="key"
          label="Key *"
          placeholder="key_name"
          autoComplete="off"
          disabled={!isCreate}
          fullWidth
        />
        {hasKeyError || isReadOnly ? null : (
          <Typography variant="body2" css={tw`text-shades-500`}>
            {isCreate ? KEY_HINT_CREATE : KEY_HINT_EDIT}
          </Typography>
        )}
      </div>
      <div css={tw`grid grid-cols-2 gap-4`}>
        <FormikSelect<RemoteConfigFormValues>
          name="app"
          label="App *"
          options={APP_OPTIONS}
          disabled={!isCreate}
          fullWidth
        />
        <FormikSelect<RemoteConfigFormValues>
          name="type"
          label="Type *"
          options={TYPE_OPTIONS}
          disabled={!isCreate}
          onChange={handleTypeChange}
          fullWidth
        />
      </div>
      <FormikTextArea<RemoteConfigFormValues>
        name="description"
        label="Description"
        placeholder="What the app does with this value"
        minRows={2}
        disabled={isReadOnly}
        fullWidth
      />
      <RemoteConfigValueEditor isReadOnly={isReadOnly} onFormat={handleFormat} />
      {isReadOnly ? null : (
        <Typography variant="body2" css={tw`text-shades-500`}>
          {VALUE_VISIBILITY_HINT}
        </Typography>
      )}
      <div css={tw`border-0 border-t border-solid border-shades-100 pt-4`}>
        <FormikSwitch<RemoteConfigFormValues>
          name="enabled"
          label="On"
          description="Off: the app doesn't get this key and uses its in-code default."
          disabled={isReadOnly}
        />
      </div>
      {formStatus?.formError ? (
        <Typography variant="body2" role="alert" css={tw`text-critical-500`}>
          {formStatus.formError}
        </Typography>
      ) : null}
    </div>
  )
}

export const RemoteConfigFormContent = memo(RemoteConfigFormContentComponent)
